/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {AssetState} from '@ion/core/src/types/entities';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import useThemeContext from '../theme/useThemeContext';
import {FilterOption, FilterPropsType} from '../types/types';
import Filter from './Filter';
import {getMenuStyles} from './FilterHelpers';
import {FilterParamsProps} from './useFilterSubmit';

// defines the prop definitions for AssetStatusFilter ...
export interface AssetStatusFilterProps extends FilterPropsType {};

// constructs the asset status options as key value pairs - labels are
// uppercased by default (legacy); `keepLabelCase` (current theme) keeps the
// translated label as-is, per the DLS Filter Tag ...
export const getAssetStatusOptions = (keepLabelCase = false) => {
  const assetStatus   = translate('common:assetState', {returnObjects: true}); ;
  const statusAllowed = [];
  if (!isEmpty(assetStatus)) {
    for (const [value, label] of Object.entries(assetStatus)) {
      if (AssetState[value?.toUpperCase()]) {
        statusAllowed.push({
          value : value?.toUpperCase(),
          label : keepLabelCase ? label : label?.toUpperCase()
        });
      }
    }
  }
  return statusAllowed?.sort((a, b) => a?.label - b?.label);
};

/**
 * The AssetStatusFilter component is a reusable filter for managing the filtering
 * of asset status, and can be custom-styled with props.
 *
 * ```typescript
 *
 * import AssetStatusFilter from '@ion/dls/src/filters';
 *
 * <AssetStatusFilter elementId={elementId}
 *   name={name}
 *   filterState={filterState}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onMenuClose={onMenuClose}
 *   primaryButtonDisabled={primaryButtonDisabled}
 *   secondaryButtonDisabled={secondaryButtonDisabled}
 *   showSelectedOptionsCount={false}
 *   isMobileView= {false} />
 *
 * ```
 */
export const AssetStatusFilter = ({
  elementId,
  name,
  onSubmit,
  onCancel,
  onMenuClose,
  primaryButtonDisabled,
  secondaryButtonDisabled,
  filterState = {},
  showSelectedOptionsCount = true,
  isMobileView = false,
  accountId
}: AssetStatusFilterProps) => {
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const {
    loading: isLoading,
    error: hasErrors,
    selected: selectedOptions
  } = filterState;
  const {
    menuLeft,
    menuWidth,
    contentGridTemplateColumns,
    contentJustifyContent,
    contentGridColumnGap,
    contentGridRowGap
  } = getMenuStyles(isMobileView);

  function handleSubmit({selections, options, loading, hasError}) {
    const filterParams: FilterParamsProps = {
      optionsKey    : FilterOption.value,
      selectionsKey : FilterOption.value,
      options,
      selections,
      loading,
      hasError
    };
    onSubmit?.(name, filterParams);
  };

  const loadFilterOptions = async function() {
    const assetStatusOptions = getAssetStatusOptions(isCurrentTheme);
    return {
      result       : assetStatusOptions,
      filterResult : assetStatusOptions
    };
  };

  return (
    <Filter optionType={translate('common:filters.assetStatus')}
      optionPropId={FilterOption?.value}
      optionPropLabel={FilterOption?.label}
      selectedOptionIds={selectedOptions || []}
      loading={isLoading}
      showSelectedOptionsCount={showSelectedOptionsCount}
      primaryButtonDisabled={primaryButtonDisabled}
      secondaryButtonDisabled={secondaryButtonDisabled}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onMenuClose={onMenuClose}
      elementId={elementId}
      menuLeft={menuLeft}
      menuWidth={menuWidth}
      contentGridTemplateColumns={contentGridTemplateColumns}
      contentJustifyContent={contentJustifyContent}
      contentGridColumnGap={contentGridColumnGap}
      contentGridRowGap={contentGridRowGap}
      loadFilterOptions={loadFilterOptions}
      accountId={accountId}
      isMobileView={isMobileView} />
  );
};

export default AssetStatusFilter;
